import React, { useState, useEffect, useCallback } from 'react';
import { Input } from 'reactstrap';
import { NavLink, HashRouter } from 'react-router-dom';
import Customer from './CustomerForm';
import SweetAlert from 'react-bootstrap-sweetalert';
import CustomerPlanForm from './CustomerPlanForm';
import { disableCustomer, exportCustomerTelemetry } from '../../actions/ActionCustomer';
import oClasses from './CustomerIndex.module.scss';
import Select from 'react-select';
import { getPartnerOptions } from '../../actions/ActionPartnerWL';
import CustomerList from './CustomerList';
import { useCustomerListContext, CustomerListProvider } from './CustomerListContext';
import CustomerPlanEndEditModal from './CustomerPlanEndEditModal';
import CustomerTelemetryModal from './CustomerTelemetryModal';
import { can } from '../../utils/permissions';
import CustomerTemplateImportModal from './CustomerTemplateImportModal';
import { tCustomerTemplate } from '../../i18n/customerTemplateImport';
import CustomerInviteModal from './CustomerInviteModal';

const CustomerPage = ({ setAlert }) => {
  const [cModalSelected, setModalSelected] = useState(null);
  const [oCustomerActive, setCustomerActive] = useState(null);
  const [bIsExportingTelemetry, setIsExportingTelemetry] = useState(false);

  const { getNextPage } = useCustomerListContext();

  const openModal = (cType, oCustomer) => {
    setModalSelected(cType);
    setCustomerActive(oCustomer);
  };

  const onCloseModal = () => {
    setModalSelected(null);
    setCustomerActive(null);
  };

  const onCustomerSave = (cType, cMsg) => {
    onCloseModal();
    getNextPage(true);
    setAlert(cType, cMsg);
  };

  const onCustomerDisable = async oCustomer => {
    try {
      const cMessage = await disableCustomer(oCustomer.cust_nid);
      onCloseModal();
      setAlert('success', cMessage);
      getNextPage(true);
    } catch (oError) {
      onCloseModal();
      setAlert('error', oError.aMsgErrors);
    }
  };

  const getExportFilename = oResponse => {
    const cDisposition = oResponse.headers['content-disposition'] || '';
    const aFilenameMatch = cDisposition.match(/filename="?([^"]+)"?/i);

    return aFilenameMatch ? aFilenameMatch[1] : 'telemetria-clientes.csv';
  };

  const downloadBlob = (oBlob, cFilename) => {
    const cUrl = window.URL.createObjectURL(oBlob);
    const oLink = document.createElement('a');
    oLink.href = cUrl;
    oLink.setAttribute('download', cFilename);
    document.body.appendChild(oLink);
    oLink.click();
    oLink.remove();
    window.URL.revokeObjectURL(cUrl);
  };

  const onExportTelemetry = async () => {
    try {
      setIsExportingTelemetry(true);
      const oResponse = await exportCustomerTelemetry();
      downloadBlob(oResponse.data, getExportFilename(oResponse));
    } catch (oError) {
      setAlert('error', oError.aMsgErrors || ['Erro ao exportar telemetria.']);
    } finally {
      setIsExportingTelemetry(false);
    }
  };

  const getOpenModal = useCallback(() => {
    switch (cModalSelected) {
      case 'customerEdit':
        return (
          <CustomerEditModal oCustomer={oCustomerActive} onClose={onCloseModal} onSave={onCustomerSave} />
        );
      case 'customerExtract':
        return (
          <CustomerExtractModal oCustomer={oCustomerActive} onClose={onCloseModal} onSave={onCustomerSave} />
        );
      case 'customerDisable':
        return (
          <CustomerDeactivateModal oCustomer={oCustomerActive} onClose={onCloseModal} onSave={onCustomerDisable} />
        );
      case 'customerPlanEnd':
        return (
          <CustomerPlanEndModal oCustomer={oCustomerActive} onClose={onCloseModal} onSave={onCustomerSave} />
        );
      case 'customerTelemetry':
        return (
          <CustomerTelemetrySweetAlertModal oCustomer={oCustomerActive} onClose={onCloseModal} />
        );
      case 'customerTemplateImport':
        return (
          <CustomerTemplateImportSweetAlertModal
            oCustomer={oCustomerActive}
            onClose={onCloseModal}
            setAlert={setAlert}
          />
        );
      case 'customerInvite':
        return (
          <CustomerInviteSweetAlertModal
            onClose={onCloseModal}
            setAlert={setAlert}
          />
        );
      default:
        return null;
    }
  }, [
    cModalSelected,
    oCustomerActive,
    onCloseModal,
    onCustomerDisable,
    onCustomerSave,
    setAlert,
  ]);

  return (
    <div>
      <div className="title-button">
        <h1>Lista de Clientes</h1>

        <div className="button-stack">
          {can('view-customer') && (
            <button
              type="button"
              className={`btn alternate ${bIsExportingTelemetry ? 'disabled' : ''}`}
              onClick={onExportTelemetry}
              disabled={bIsExportingTelemetry}
            >
              {bIsExportingTelemetry ? 'Exportando...' : 'Exportar telemetria'}
            </button>
          )}

          {can('edit-customer') && (
            <button
              type="button"
              className="btn alternate"
              onClick={() => openModal('customerInvite', null)}
            >
              {tCustomerTemplate('customerTemplate.inviteAction')}
            </button>
          )}

          {can('edit-customer') && (
            <HashRouter>
            <NavLink className="btn alternate" to="/Customer/Create" title="Cliente">
              Criar cliente
            </NavLink>
          </HashRouter>
          )}
        </div>
      </div>
      {getOpenModal()}

      {can('view-customer') && (
        <>
        <div className={oClasses.listControls} >
          <CustomerSearchForm />
          <PartnerWLSelect />
        </div>

        <CustomerList setOpenModal={openModal} />
        </>
      )}
    </div>
  );
};

const CustomerSearchForm = () => {
  const [cSearchVal, setSearchVal] = useState('');
  const { setSearch } = useCustomerListContext();
  const [oDebounceTimer, setDebounceTimer] = useState(null);

  const updateSearch = (evtChange) => {
    const cValue = evtChange.target.value;
    setSearchVal(cValue);

    if (oDebounceTimer) {
      clearTimeout(oDebounceTimer);
    }
    setDebounceTimer(setTimeout(() => {
      setSearch(cValue);
    }, 500));
  };

  return (
    <Input
      type="search"
      className={oClasses.searchInput}
      onChange={updateSearch}
      value={cSearchVal}
      placeholder="Pesquisar..."
    />
  );
}

const PartnerWLSelect = () => {
  const oOptionShowAll = { label: 'Todos', value: null };
  const [aWlPartnerOptions, setWlPartnerOptions] = useState([oOptionShowAll]);
  const { setWlpnId } = useCustomerListContext();

  const getPartners = useCallback(async () => {
    try {
      const aPartners = await getPartnerOptions();
      setWlPartnerOptions([oOptionShowAll, ...aPartners]);
    } catch (oError) {
      console.log(oError);
    }
  }, [setWlPartnerOptions]);

  const updateSelectedPartner = useCallback((oOption) => {
    setWlpnId(oOption.value);
  }, [setWlpnId]);

  useEffect(() => {
    getPartners();
  }, []);

  return (
    <Select
      className={`selectCustom ${oClasses.partnerList}`}
      classNamePrefix="react-select"
      noOptionsMessage={() => ''}
      placeholder="Selecione"
      options={aWlPartnerOptions}
      maxMenuHeight={190}
      defaultValue={oOptionShowAll}
      onChange={updateSelectedPartner}
    />
  );
}

const CustomerEditModal = ({ oCustomer, onClose = () => {}, onSave = () => {} }) => {
  return (
    <SweetAlert customClass="modal-edit lg" title="" onConfirm={onClose} showConfirm={false}>
      <div className="head-modal">
        Editar cliente
        <span role="button" tabIndex="0" onClick={onClose} className="close" />
      </div>
      <Customer bIsEditing bIsModal nCustId={oCustomer.cust_nid} fnHandleClose={onClose} fnHandleConfirm={onSave} />
    </SweetAlert>
  );
};

const CustomerExtractModal = ({ oCustomer, onClose = () => {}, onSave = () => {} }) => {
  return (
    <SweetAlert customClass="modal-edit lg" title="" onConfirm={onClose} showConfirm={false}>
      <div className="head-modal">
        Extrato Financeiro
        <span role="button" tabIndex="0" onClick={onClose} className="close" />
      </div>
      <CustomerPlanForm nCustId={oCustomer.cust_nid} fnOnClose={onClose} fnOnSubmit={onSave} />
    </SweetAlert>
  );
};

const CustomerPlanEndModal = ({ oCustomer, onClose = () => {}, onSave = () => {} }) => {
  return (
    <SweetAlert customClass="modal-edit lg" title="" onConfirm={onClose} showConfirm={false}>
      <div className="head-modal">
        Alterar vencimento do plano ({oCustomer.cust_nid} - {oCustomer.cust_cname})
        <span role="button" tabIndex="0" onClick={onClose} className="close" />
      </div>
      <CustomerPlanEndEditModal nCustId={oCustomer.cust_nid} fnOnClose={onClose} fnOnSubmit={onSave} />
    </SweetAlert>
  );
};

const CustomerTelemetrySweetAlertModal = ({ oCustomer, onClose = () => {} }) => {
  return (
    <SweetAlert customClass="modal-edit lg" title="" onConfirm={onClose} showConfirm={false}>
      <div className="head-modal">
        Telemetria do cliente ({oCustomer.cust_nid} - {oCustomer.cust_cname})
        <span role="button" tabIndex="0" onClick={onClose} className="close" />
      </div>
      <CustomerTelemetryModal nCustId={oCustomer.cust_nid} />
    </SweetAlert>
  );
};

/**
 * Abre o acompanhamento da importacao de modelo dentro do padrao de modal da tela.
 */
const CustomerTemplateImportSweetAlertModal = ({ oCustomer, onClose = () => {}, setAlert = () => {} }) => {
  return (
    <SweetAlert customClass="modal-edit lg" title="" onConfirm={onClose} showConfirm={false}>
      <div className="head-modal">
        {tCustomerTemplate('customerTemplate.importTitle')} ({oCustomer.cust_nid} - {oCustomer.cust_cname})
        <span role="button" tabIndex="0" onClick={onClose} className="close" />
      </div>
      <CustomerTemplateImportModal nCustId={oCustomer.cust_nid} setAlert={setAlert} />
    </SweetAlert>
  );
};

/**
 * Abre o formulario de convite com modelo no padrao visual existente.
 */
const CustomerInviteSweetAlertModal = ({ onClose = () => {}, setAlert = () => {} }) => {
  return (
    <SweetAlert customClass="modal-edit md" title="" onConfirm={onClose} showConfirm={false}>
      <div className="head-modal">
        {tCustomerTemplate('customerTemplate.inviteTitle')}
        <span role="button" tabIndex="0" onClick={onClose} className="close" />
      </div>
      <CustomerInviteModal onClose={onClose} setAlert={setAlert} />
    </SweetAlert>
  );
};

const CustomerDeactivateModal = ({ oCustomer, onClose = () => {}, onSave = () => {} }) => {
  return (
    <SweetAlert
      danger
      showCancel
      confirmBtnText={'Confirma'}
      cancelBtnText={'Cancelar'}
      cancelBtnBsStyle="danger"
      title={''}
      onConfirm={() => onSave(oCustomer)}
      onCancel={onClose}
    >
      <p>Deseja realmente desativar o cliente: {oCustomer.cust_cname}</p>
    </SweetAlert>
  );
};

const CustomerIndex = ({ setAlert }) => {
  return (
    <CustomerListProvider>
      <CustomerPage setAlert={setAlert} />
    </CustomerListProvider>
  );
};

export default CustomerIndex;
